'use client';

import { useState } from 'react';
import { Send, Loader2, Plus, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { conversationClient } from '@/lib/gupshup-client';

type Button = {
  id: string;
  title: string;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conversationId?: string;
  phoneNumber?: string;
  onMessageSent?: () => void;
};

export function InteractiveMessageDialog({
  open,
  onOpenChange,
  conversationId,
  phoneNumber,
  onMessageSent,
}: Props) {
  const { t } = useTranslation('conversations');
  const [header, setHeader] = useState('');
  const [body, setBody] = useState('');
  const [buttons, setButtons] = useState<Button[]>([
    { id: 'button_1', title: '' }
  ]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAddButton = () => {
    if (buttons.length < 3) {
      setButtons([
        ...buttons,
        { id: `button_${buttons.length + 1}`, title: '' }
      ]);
    }
  };

  const handleRemoveButton = (index: number) => {
    if (buttons.length > 1) {
      setButtons(buttons.filter((_, i) => i !== index));
    }
  };

  const handleButtonTitleChange = (index: number, title: string) => {
    // WhatsApp limit: 20 characters
    if (title.length <= 20) {
      const newButtons = [...buttons];
      newButtons[index].title = title;
      setButtons(newButtons);
    }
  };

  const isValid = () => {
    if (!body.trim()) return false;
    if (buttons.length === 0) return false;
    if (buttons.some(btn => !btn.title.trim())) return false;
    return true;
  };

  const handleReset = () => {
    setHeader('');
    setBody('');
    setButtons([{ id: 'button_1', title: '' }]);
    setError(null);
  };

  const handleSend = async () => {
    if (!isValid()) {
      setError(t('interactive.errors.fillRequired'));
      return;
    }

    if (!conversationId || !phoneNumber) {
      setError(t('interactive.errors.noConversation'));
      return;
    }

    setSending(true);
    setError(null);

    try {
      await conversationClient.messages.sendInteractiveButtons({
        to: phoneNumber,
        body: body.trim(),
        header: header.trim() || undefined,
        buttons: buttons.map(btn => ({
          id: btn.id,
          title: btn.title.trim()
        })),
        conversationId,
      });

      handleReset();
      onOpenChange(false);
      onMessageSent?.();
    } catch (err) {
      console.error('Error sending interactive message:', err);
      setError(err instanceof Error ? err.message : t('interactive.errors.sendFailed'));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(open) => {
      onOpenChange(open);
      if (!open) handleReset();
    }}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('interactive.title')}</DialogTitle>
          <DialogDescription>{t('interactive.description')}</DialogDescription>
        </DialogHeader>

        {error && (
          <div className="p-3 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800/60 rounded-lg text-sm text-red-800 dark:text-red-300">
            {error}
          </div>
        )}

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="header" className="text-foreground">
              {t('interactive.headerLabel')}
            </Label>
            <Input
              id="header"
              value={header}
              onChange={(e) => setHeader(e.target.value)}
              placeholder={t('interactive.headerPlaceholder')}
              className="bg-background border-input focus-visible:ring-ring"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="body" className="text-foreground">
              {t('interactive.bodyLabel')} <span className="text-red-500 dark:text-red-400">*</span>
            </Label>
            <Textarea
              id="body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={t('interactive.bodyPlaceholder')}
              className="bg-background border-input focus-visible:ring-ring min-h-[100px]"
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-foreground">
                {t('interactive.buttonsLabel')} <span className="text-red-500 dark:text-red-400">*</span>
              </Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleAddButton}
                disabled={buttons.length >= 3}
                className="h-8 text-primary hover:text-primary/90 hover:bg-muted"
              >
                <Plus className="h-4 w-4 mr-1" />
                {t('interactive.addButton')}
              </Button>
            </div>

            <div className="space-y-2">
              {buttons.map((button, index) => (
                <div key={button.id} className="flex items-center gap-2">
                  <Input
                    value={button.title}
                    onChange={(e) => handleButtonTitleChange(index, e.target.value)}
                    placeholder={t('interactive.buttonTitlePlaceholder', { number: index + 1 })}
                    className="bg-background border-input focus-visible:ring-ring"
                    maxLength={20}
                  />
                  <span className="text-xs text-muted-foreground min-w-[3rem]">
                    {button.title.length}/20
                  </span>
                  {buttons.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={t('interactive.removeButtonAria')}
                      onClick={() => handleRemoveButton(index)}
                      className="h-9 w-9 text-muted-foreground hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>

            {buttons.length < 3 && (
              <p className="text-xs text-muted-foreground">
                {t('interactive.addMoreHint', { count: 3 - buttons.length })}
              </p>
            )}
          </div>
        </div>

        <Separator />

        <div className="flex justify-between gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('interactive.cancel')}
          </Button>
          <Button
            onClick={handleSend}
            disabled={!isValid() || sending}
            className="bg-primary hover:bg-primary/90"
          >
            {sending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Send className="h-4 w-4 mr-1" />
                {t('interactive.send')}
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
